'use client';

import { CallOffOrderColor, QuotedColor, SmallLotPricing } from '@ravago/shared/page-data/models/elements';
import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';

import { useOrderNowV2State } from '../../hooks/use-order-now-v2-state';
import { Address } from '@ravago/shared/page-data/models/elements/address.model';

import { CallOffDetailView } from './call-off-detail-view';
import { OrderNowV2Selector } from './order-now-v2-selector';
import { PricingDetailView } from './pricing-detail-view';
import { QuoteDeliveryAddressSelectionView } from './quote-delivery-address-selection-view';
import { SmallLotPriceView } from './small-lot-price-view';

interface Props {
   callOffOrderColors: CallOffOrderColor[];
   quotedColors: QuotedColor[];
   smallLotPrices: SmallLotPricing[];
   productName: string;
   quoteDetailRoute: string;
   callOffAgreementRoute?: string;
   usesAgreementApp: boolean;
   bracketPricingEnabled: boolean;
   leadTimeIndicators: LeadTimeIndicator[];
   leadTimeEnabled: boolean;
   smallLotDefaultAddress?: Address;
   onRequestQuote: () => void;
   onContinueCallOff: (callOffOrderColor: CallOffOrderColor, quantity: number) => void;
}

export const OrderNowV2 = ({
   callOffOrderColors,
   quotedColors,
   smallLotPrices,
   productName,
   quoteDetailRoute,
   callOffAgreementRoute,
   usesAgreementApp,
   bracketPricingEnabled,
   leadTimeIndicators,
   leadTimeEnabled,
   smallLotDefaultAddress,
   onRequestQuote,
   onContinueCallOff
}: Props) => {
   const {
      callOffGroups,
      quotedColorGroups,
      filteredSmallLotPrices,
      uniqueSmallLotColors,
      colorCodesForSelectedColor,
      selectedCallOffKey,
      selectedColorGroupKey,
      selectedSmallLotColorId,
      selectedSmallLotProducerColorCode,
      selectedCallOffGroup,
      selectedColorGroup,
      selectedPricing,
      selectedDeliveryAddressId,
      flatLines,
      quoteDeliveryAddressOptions,
      smallLotPricingForSelectedColorGroup,
      hasMultipleSelections,
      step,
      handleCallOffGroupSelect,
      handleQuotedColorGroupSelect,
      handleSmallLotColorChange,
      handleSmallLotColorCodeChange,
      handleViewCallOff,
      handleViewQuote,
      handleSelectDeliveryAddress,
      handleContinueQuoteWithDeliveryAddress,
      handleViewPrices,
      handleEditClick
   } = useOrderNowV2State({ callOffOrderColors, quotedColors, smallLotPrices, bracketPricingEnabled });

   if (step === 'call-off-detail' && selectedCallOffGroup) {
      return (
         <CallOffDetailView
            selectedCallOffGroup={selectedCallOffGroup}
            usesAgreementApp={usesAgreementApp}
            callOffAgreementRoute={callOffAgreementRoute}
            hasMultipleSelections={hasMultipleSelections}
            onEdit={handleEditClick}
            onContinueCallOff={onContinueCallOff}
         />
      );
   }

   if (step === 'small-lot-price-view' && selectedPricing) {
      return (
         <SmallLotPriceView
            pricing={selectedPricing}
            productName={productName}
            hasMultipleSelections={hasMultipleSelections}
            leadTimeIndicators={leadTimeIndicators}
            leadTimeEnabled={leadTimeEnabled}
            smallLotDefaultAddress={smallLotDefaultAddress}
            onEdit={handleEditClick}
            onRequestQuote={onRequestQuote}
         />
      );
   }

   if (step === 'quote-detail' && selectedColorGroup) {
      return (
         <PricingDetailView
            selectedColorGroup={selectedColorGroup}
            flatLines={flatLines}
            productName={productName}
            smallLotPricingForColorGroup={smallLotPricingForSelectedColorGroup}
            quoteDetailRoute={quoteDetailRoute}
            hasMultipleSelections={hasMultipleSelections}
            bracketPricingEnabled={bracketPricingEnabled}
            leadTimeIndicators={leadTimeIndicators}
            leadTimeEnabled={leadTimeEnabled}
            selectedDeliveryAddressId={selectedDeliveryAddressId}
            smallLotDefaultAddress={smallLotDefaultAddress}
            onEdit={handleEditClick}
            onRequestQuote={onRequestQuote}
         />
      );
   }

   if (step === 'quote-delivery-address-selection' && selectedColorGroup) {
      return (
         <QuoteDeliveryAddressSelectionView
            selectedColorGroup={selectedColorGroup}
            deliveryAddressOptions={quoteDeliveryAddressOptions}
            selectedDeliveryAddressId={selectedDeliveryAddressId}
            hasMultipleSelections={hasMultipleSelections}
            bracketPricingEnabled={bracketPricingEnabled}
            leadTimeIndicators={leadTimeIndicators}
            leadTimeEnabled={leadTimeEnabled}
            onEdit={handleEditClick}
            onSelectDeliveryAddress={handleSelectDeliveryAddress}
            onContinue={handleContinueQuoteWithDeliveryAddress}
         />
      );
   }

   return (
      <OrderNowV2Selector
         callOffGroups={callOffGroups}
         quotedColorGroups={quotedColorGroups}
         smallLotPrices={filteredSmallLotPrices}
         smallLotPricesForLeadTime={smallLotPrices}
         smallLotDefaultAddress={smallLotDefaultAddress}
         uniqueSmallLotColors={uniqueSmallLotColors}
         colorCodesForSelectedColor={colorCodesForSelectedColor}
         selectedCallOffKey={selectedCallOffKey}
         selectedColorGroupKey={selectedColorGroupKey}
         selectedSmallLotColorId={selectedSmallLotColorId}
         selectedSmallLotProducerColorCode={selectedSmallLotProducerColorCode}
         bracketPricingEnabled={bracketPricingEnabled}
         leadTimeIndicators={leadTimeIndicators}
         leadTimeEnabled={leadTimeEnabled}
         onCallOffGroupChange={handleCallOffGroupSelect}
         onColorGroupChange={handleQuotedColorGroupSelect}
         onSmallLotColorChange={handleSmallLotColorChange}
         onSmallLotColorCodeChange={handleSmallLotColorCodeChange}
         onViewCallOff={handleViewCallOff}
         onViewQuote={handleViewQuote}
         onViewPrices={handleViewPrices}
         onRequestQuote={onRequestQuote}
      />
   );
};
